import React from 'react';
import classNames from 'classnames';
import { Icon } from 'js/design_system/Icon';
import { useRandomId } from 'js/utils/hooks/useRandomId';

import 'app/assets/stylesheets/design_system/checkbox.scss';

export type CheckboxProps = {
  /**
   * Whether the checkbox is checked
   */
  checked?: boolean;
  /**
   * Callback when checkbox state changes
   */
  onChange?: (checked: boolean) => void;
  /**
   * Label text to display next to the checkbox
   */
  label?: React.ReactNode;
  /**
   * Whether the checkbox is disabled
   */
  disabled?: boolean;
  /**
   * Size of the checkbox
   */
  size?: 'sm' | 'lg';
  /**
   * Optional className for the wrapper
   */
  className?: string;
  /**
   * ID for the input element (auto-generated if not provided)
   */
  id?: string;
} & Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'onChange' | 'size' | 'type' | 'checked'
>;

/**
 * A checkbox component for boolean selection states.
 *
 * ## Basic Usage
 *
 * @example
 *
 * ```tsx
 * import { Checkbox } from 'js/design_system/forms/Checkbox';
 *
 * const [checked, setChecked] = useState(false);
 *
 * <Checkbox
 *   checked={checked}
 *   onChange={setChecked}
 *   label="Accept terms and conditions"
 * />
 * ```
 */
export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      checked = false,
      onChange,
      label,
      disabled = false,
      size = 'lg',
      className,
      id: providedId,
      ...restProps
    },
    ref,
  ) => {
    const generatedId = useRandomId();
    const id = providedId ?? `checkbox-${generatedId}`;

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!disabled) {
        onChange?.(event.target.checked);
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLLabelElement>) => {
      if (event.key === ' ' || event.key === 'Enter') {
        event.preventDefault();
        if (!disabled) {
          onChange?.(!checked);
        }
      }
    };

    return (
      <label
        className={classNames('ds-checkbox', className, {
          'ds-checkbox--small': size === 'sm',
          'ds-checkbox--disabled': disabled,
          'ds-checkbox--checked': checked,
        })}
        htmlFor={id}
        onKeyDown={handleKeyDown}
      >
        <input
          {...restProps}
          ref={ref}
          type="checkbox"
          id={id}
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          className="ds-checkbox__input"
        />
        <span className="ds-checkbox__box" aria-hidden="true">
          {checked && (
            <Icon
              name="check"
              size={size === 'sm' ? 12 : 14}
              className="ds-checkbox__check"
            />
          )}
        </span>
        {label && <span className="ds-checkbox__label">{label}</span>}
      </label>
    );
  },
);

Checkbox.displayName = 'Checkbox';
